import React, { useState, useEffect } from "react";
import PopupForm from "./PopupForm.js";
import ServiceNav from "../components/ServiceNav";
import ServiceHero from "../components/ServiceHero";
import { FiCheckCircle, FiFileText, FiClock } from "react-icons/fi";
import { ServiceCta } from "../components/CtaSystem";
import "./GstReconciliation.css";

const services = [
  {
    title: "Enhance Your GST Reconciliation Process",
    description:
      "We provide hassle-free and advanced GST reconciliation solutions to reduce compliance burden. Our system ensures **100% ITC claims** by identifying and matching entries accurately.",
  },
  {
    title: "Challenges in GST Reconciliation",
    description:
      "GST reconciliation faces common issues like GSTIN mismatches, invoice errors, timing differences, rounding off variations, and unfiled returns. Our system detects and resolves these challenges.",
  },
  {
    title: "Our Automated Solution",
    description:
      "Our AI-powered system auto-identifies mismatched data, integrates with ERP systems, handles high-volume invoices efficiently, and corrects supplier-side errors.",
  },
  {
    title: "Real-Time Invoice Matching",
    description:
      "Automatically match invoices across multiple return periods to ensure compliance. Identify discrepancies before filing returns and avoid penalties.",
  },
  {
    title: "Final Reconciliation Reports",
    description:
      "Our system generates categorized reports: **Matched, Not in GSTR-2A, Not in Purchase Register, and Mismatched**, ensuring a smooth reconciliation process.",
  }
];

const GSTReconciliation = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const openPDF = () => {
    window.open("/GST Advance Reco - DataBoltaHi.pdf", "_blank");
  };

  const nextService = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % services.length);
  };

  const prevService = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? services.length - 1 : prevIndex - 1
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        nextService();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isHovered]);

  return (
    <div className="gst-reconciliation-container">
      {/* Hero Section */}
      <ServiceHero
        kicker="SERVICE 03"
        title="GST Reconciliation"
        description="Automate and simplify GST return filings and reconciliation processes."
        bgImage="/service3.png"
        onConnectClick={() => setIsPopupOpen(true)}
        onPdfClick={openPDF}
      />

      {/* About GST Reconciliation */}
      <section className="about-gst-section">
        <div className="about-content">
          <div className="vector-image">
            <img src="./gst-img.png" alt="GST Reconciliation" />
          </div>

          <div className="about-text">
            <h2>What is GST Reconciliation?</h2>
            <p>
              GST reconciliation is the process of matching the data filed by your suppliers 
              with your purchase records to ensure accurate tax filings and avoid penalties.
            </p>
            <ul>
              <li><FiCheckCircle className="check-icon" /> Automated Data Matching</li>
              <li><FiCheckCircle className="check-icon" /> GST Compliance Reports</li>
              <li><FiCheckCircle className="check-icon" /> Minimize Errors & Penalties</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <h2 className="section-title">Why Choose Our GST Reconciliation Solution?</h2>
        <div className="features-grid">
          <div className="feature-card">
            <span className="feature-icon"><FiFileText /></span>
            <h3>Automated Filing</h3>
            <p>Seamless GST return filing with automation.</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon"><FiCheckCircle /></span>
            <h3>Accurate Matching</h3>
            <p>Match invoices and tax data with high precision.</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon"><FiClock /></span>
            <h3>Save Time</h3>
            <p>Reduce errors and manual work with smart processing.</p>
          </div>
        </div>
      </section>

      {/* Carousel Section */}
      <section className="gst-carousel-section">
        <h2>Transform Your GST Reconciliation</h2>
        <div 
          className="gst-service-carousel" 
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="gst-service-card">
            <h3>{services[currentIndex].title}</h3>
            <p>{services[currentIndex].description}</p>
            <div className="carousel-controls">
              <button onClick={prevService}>&#9665;</button>
              <button onClick={nextService}>&#9655;</button>
            </div>
          </div>
        </div>
      </section>

      {/* Service CTA */}
      <ServiceCta
        eyebrow="GST RECONCILIATION"
        heading="Automate GSTR-2A/2B Matching & Optimize Input Tax Credit"
        primaryBtnText="Connect Now!"
        onPrimaryClick={() => setIsPopupOpen(true)}
        secondaryBtnText="Contact Us"
        secondaryBtnLink="/contact-us"
      />

      {/* Service Navigation */}
      <ServiceNav currentPath="/gst-reconciliation" />

      {/* Popup Form Component */}
      <PopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default GSTReconciliation;
